import { FC, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocalStorage } from 'react-use';
import { twMerge } from 'tailwind-merge';

import {
  Button,
  FloatingTabs,
  Icon,
  Input,
  Link,
  RyeconCopy,
  RyeconLinkExternal,
  Sizes,
} from '@dbt-labs/sourdough';

import { AutoExposureBiProvider } from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { exposureTile } from '@/util/linkGenerators';
import { CollapsibleSection } from './CollapsibleSection';

type EmbedDataHealthInstructionsProps = {
  uniqueId: string;
  dashboardUri: string | undefined | null;
  className?: string;
  biResourceId: string | undefined | null;
  autoBiProvider: AutoExposureBiProvider | undefined | null;
};

export const EmbedDataHealthInstructions: FC<EmbedDataHealthInstructionsProps> = ({
  uniqueId,
  dashboardUri,
  className,
  biResourceId,
  autoBiProvider,
}) => {
  const { accountId, account } = useDbtCloudAccount();
  const { environmentId } = useDbtCloudProject();
  const getServiceTokenLink = useLinkToDbtCloud('serviceToken');
  const serviceTokenLink =
    accountId && getServiceTokenLink ? getServiceTokenLink({ accountId }) : null;

  const { environmentType } = useUrlProjectParams();

  const publicDiscoveryUri = account?.discovery_api_url;
  const publicUri = publicDiscoveryUri?.replaceAll('/internal', '');
  const exposureTileUrl =
    import.meta.env.MODE === 'development'
      ? (import.meta.env.VITE_EXPOSURE_TILE_URL ?? publicUri)
      : publicUri;

  const [serviceToken, setServiceToken] = useLocalStorage(
    'explorer-codex-service-token',
    '',
  );

  const embedUrl = exposureTile({
    environmentId,
    environmentType,
    metadataUri: exposureTileUrl,
    token: serviceToken || '<YOUR_METADATA_TOKEN>',
    uniqueId,
    biResourceId: biResourceId || '',
    autoBiProvider,
  });
  const [currentTab, setCurrentTab] = useState<'url' | 'iframe'>('url');

  const toCopyToClipboard = useMemo(() => {
    if (currentTab === 'url') return embedUrl;
    return `<iframe src='${embedUrl}' title='Exposure Status Tile'></iframe>`;
  }, [embedUrl, currentTab]);

  if (!serviceTokenLink) return null;
  return (
    <div
      className={twMerge(
        'mt-1 rounded border border-borderMuted bg-bgBackground p-4 pt-5',
        className,
      )}
    >
      <CollapsibleSection
        children={
          <div className="align-middle font-semibold">
            Embed data health into your dashboard
          </div>
        }
        toExpand={
          <div className="space-y-4 px-7 pb-2 text-sm">
            <div>
              You have the ability to embed this data health view into your dashboards
              to always ensure your data is fresh. You can do so by following the three
              simple steps below.
            </div>
            <div className="space-y-2">
              <div>
                1. First, gather your Metadata Token you can find in the link below.
              </div>
              <Link
                buttonStyleType="secondary"
                isInternal={false}
                to={serviceTokenLink}
                className="font-semibold"
                buttonSize={Sizes.sm}
              >
                <Icon ryecon={RyeconLinkExternal} />
                Get metadata token
              </Link>
            </div>
            <div className="space-y-2">
              <div>
                2. Once you have copied your token, paste it in the form below. Then,
                copy the new URL and paste it into your dashboard provider.
              </div>
              <Input
                name="token"
                title="Metadata token"
                label="Metadata token"
                placeholder="<Paste your Metadata Token here>"
                className="max-w-64"
                isEdit
                value={serviceToken}
                onChange={(e) => setServiceToken(e.target.value)}
              />
            </div>
            <FloatingTabs>
              <FloatingTabs.Tab
                id="url"
                isActive={currentTab === 'url'}
                onClick={() => setCurrentTab('url')}
              >
                URL
              </FloatingTabs.Tab>
              <FloatingTabs.Tab
                id="iframe"
                isActive={currentTab === 'iframe'}
                onClick={() => setCurrentTab('iframe')}
              >
                iFrame
              </FloatingTabs.Tab>
            </FloatingTabs>
            <div>
              <div className="space-y-4 rounded bg-black p-4 text-white">
                <div className="overflow-hidden whitespace-pre-wrap text-balance break-words font-mono">
                  {toCopyToClipboard}
                </div>
                <div>
                  <Button
                    type="secondary"
                    className="mr-4 font-semibold"
                    size={Sizes.sm}
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(toCopyToClipboard);
                      toast.success('Copied exposure tile to clipboard', {
                        id: 'copy-exposure-tile',
                      });
                    }}
                  >
                    <Icon ryecon={RyeconCopy} />
                    Copy URL
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div>
                3. Add this code snippet to your dashboard.{' '}
                <Link
                  isInternal={false}
                  target="_blank"
                  to="https://docs.getdbt.com/docs/deploy/dashboard-status-tiles"
                >
                  See guide with examples
                </Link>
                .
              </div>
              <div>
                {dashboardUri && (
                  <Link
                    buttonStyleType="secondary"
                    className="font-semibold"
                    buttonSize={Sizes.sm}
                    isInternal={false}
                    target="_blank"
                    to={dashboardUri}
                  >
                    <Icon ryecon={RyeconLinkExternal} />
                    Go to dashboard
                  </Link>
                )}
              </div>
            </div>
          </div>
        }
        closeAltText={''}
        expandAltText={''}
      ></CollapsibleSection>
    </div>
  );
};
